import { httpStatusUserMessage } from '../utils/parseApiError';

export const SESSION_EXPIRED = 'SESSION_EXPIRED' as const;
export type SessionExpiredSentinel = typeof SESSION_EXPIRED;

export const isSessionExpired = (error: unknown): boolean =>
  error instanceof Error && error.message === SESSION_EXPIRED;

const CLIENT_ERROR_PATTERNS: RegExp[] = [
  /^Failed to fetch$/i,
  /^Failed to load\b/i,
  /^NetworkError when attempting to fetch resource/i,
  /^Load failed$/i,
  /^The user aborted a request\.?$/i,
  /^AbortError$/i,
  /^TypeError:\s*Failed to fetch/i,
  /^TypeError:\s*Load failed/i,
  /^fetch failed$/i,
  /^Unable to find REST API nonce$/i,
  /^HTTP error!\s*status:/i,
  /^Request failed$/i,
];

/**
 * Turns unknown client-side failures into safe, localized UI text.
 * Keeps JSON/API `message` already carried on Error from `parseApiError` / server.
 */
export const errorToUserFacingMessage = (error: unknown, fallback: string): string => {
  if (isSessionExpired(error)) {
    return fallback;
  }
  const raw =
    error instanceof Error
      ? error.message
      : typeof error === 'object' && error !== null && 'message' in error
        && typeof (error as { message: unknown }).message === 'string'
        ? (error as { message: string }).message
        : '';
  const m = raw.trim();
  if (m === '' || m === SESSION_EXPIRED) {
    return fallback;
  }
  if (/\n\s*at\s+/.test(m) || m.length > 800) {
    return fallback;
  }
  if (CLIENT_ERROR_PATTERNS.some((re) => re.test(m))) {
    return fallback;
  }
  if (/^HTTP \d{3}$/.test(m)) {
    return httpStatusUserMessage(Number(m.slice(5)));
  }
  return m;
};

export const extractErrorMessage = (error: unknown, fallback: string): string =>
  errorToUserFacingMessage(error, fallback);
